
import { useEffect } from 'react';

interface SeoHeadProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  schemaJson?: object;
}

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

export default function SeoHead({ title, description, canonicalPath = '/', schemaJson }: SeoHeadProps) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name: string, content: string, attr = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    if (description) {
      setMeta('description', description);
      setMeta('og:description', description, 'property');
      setMeta('twitter:description', description);
    }

    if (title) {
      setMeta('og:title', title, 'property');
      setMeta('twitter:title', title);
    }

    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    setMeta('og:url', canonicalUrl, 'property');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    // Schema.org JSON-LD
    const existingScript = document.getElementById('schema-jsonld');
    if (existingScript) existingScript.remove();

    if (schemaJson) {
      const script = document.createElement('script');
      script.id = 'schema-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaJson);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById('schema-jsonld');
      if (s) s.remove();
    };
  }, [title, description, canonicalPath, schemaJson]);

  return null;
}